import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  useApiClient,
  useFetchImmutable,
  formatCountryCodesToString,
} from '@marageti/z4-lib';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel/TripMatch';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import TripMatchChipList from '../chip-list/trip-match';

const TableTripMatches = ({ tripMatches }: { tripMatches: TripMatch[] | undefined }) => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);

  const formatCurrency = (value: number | undefined | null) => (value ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value) : '-');

  const renderAmountCell = (tripMatch: TripMatch) => {
    const showTotalTripPrice = (match: TripMatch) => [
      TripMatchStatus.MatchSold,
      TripMatchStatus.MatchSoldCanceled,
      TripMatchStatus.MatchRefundedCanceled,
    ].includes(match.status);

    if (showTotalTripPrice(tripMatch)) {
      return tripMatch.totalPppd ? `${formatCurrency(tripMatch.totalPppd)} (Trip Price)` : '-';
    }
    return tripMatch.budget ? `${formatCurrency(tripMatch.budget)} (Budget)` : '-';
  };

  if (!tripMatches || tripMatches.length === 0) {
    return (
      <Stack spacing={1.5} my={6} display="flex" alignItems="center" justifyContent="center">
        <OutlinedIcons.OutlinedExclamationCircleIcon color="disabled" sx={{ width: 48, height: 48 }} />
        <Typography variant="body" color="text.secondary" sx={{ textAlign: 'center' }}>
          There are no trip matches for this traveler
        </Typography>
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 450, mt: 1, mb: 3 }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '150px' }}>Destination</TableCell>
            <TableCell sx={{ width: '120px' }}>Departure</TableCell>
            <TableCell sx={{ width: '150px' }}>Partner</TableCell>
            <TableCell sx={{ width: '150px' }}>PPPD</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tripMatches.map((tripMatch) => (
            <TableRow
              key={tripMatch.referenceId}
              onClick={() => window.open(`/trip-matches/${tripMatch.id}`, '_blank')}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                '&:last-child td, &:last-child th': { border: 0 },
              }}
              hover
            >
              <TableCell component="th" scope="row" sx={{ width: '150px' }}>
                {(countryCodes && formatCountryCodesToString(tripMatch.countries, countryCodes)) || '-'}
              </TableCell>
              <TableCell sx={{ width: '120px' }}>{getFormattedDateShortMonthDDYYYY(tripMatch.departureDate) || '-'}</TableCell>
              <TableCell sx={{ width: '150px' }}>{tripMatch.partnerName || '-'}</TableCell>
              <TableCell sx={{ width: '150px' }}>{renderAmountCell(tripMatch)}</TableCell>
              <TableCell><TripMatchChipList chips={[tripMatch.status]} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableTripMatches;
